import React, { useMemo } from 'react';
import {
  buildEngagementTrend,
  type EngagementEventRow,
  type EngagementTrendPoint,
} from '../../lib/engagementDashboard';
import {
  chartXAtPeriodIndex,
  findCurrentPeriodIndex,
  formatTrendPeriodTooltip,
  getChartAxisEndpointLabels,
  getTrendBucketMode,
  getTrendChartUnitLabel,
} from '../../lib/connectionDashboard';
import { useDashboardTrendDays, useSyncedChartHover } from '../../context/DashboardChartHoverContext';
import ChartTodayMarker from './ChartTodayMarker';
import ChartHoverHighlight from './ChartHoverHighlight';

type EngagementTrendChartProps = {
  events: EngagementEventRow[];
  className?: string;
};

const CHART_WIDTH = 400;
const CHART_HEIGHT = 160;

type SeriesCoords = {
  opens: { x: number; y: number }[];
  clicks: { x: number; y: number }[];
  opensPath: string;
  clicksPath: string;
  opensAreaPath: string;
  maxY: number;
};

function buildSeries(points: EngagementTrendPoint[]): SeriesCoords {
  if (points.length === 0) {
    return { opens: [], clicks: [], opensPath: '', clicksPath: '', opensAreaPath: '', maxY: 1 };
  }

  const maxV = Math.max(...points.flatMap((p) => [p.opens, p.clicks]), 1);
  const range = maxV;

  const opens = points.map((p, i) => {
    const x = points.length <= 1 ? CHART_WIDTH / 2 : (i / (points.length - 1)) * CHART_WIDTH;
    const y = CHART_HEIGHT - (p.opens / range) * CHART_HEIGHT;
    return { x, y };
  });

  const clicks = points.map((p, i) => {
    const x = points.length <= 1 ? CHART_WIDTH / 2 : (i / (points.length - 1)) * CHART_WIDTH;
    const y = CHART_HEIGHT - (p.clicks / range) * CHART_HEIGHT;
    return { x, y };
  });

  const opensPath = opens
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(' ');
  const clicksPath = clicks
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(' ');
  const opensAreaPath = `${opensPath} L ${opens[opens.length - 1].x.toFixed(1)} ${CHART_HEIGHT.toFixed(1)} L ${opens[0].x.toFixed(1)} ${CHART_HEIGHT.toFixed(1)} Z`;

  return { opens, clicks, opensPath, clicksPath, opensAreaPath, maxY: maxV };
}

const EngagementTrendChart: React.FC<EngagementTrendChartProps> = ({ events, className = '' }) => {
  const { dayCount } = useDashboardTrendDays();

  const points = useMemo(
    () => buildEngagementTrend(events, dayCount),
    [events, dayCount],
  );

  const bucketMode = getTrendBucketMode(dayCount);
  const axisLabels = useMemo(
    () => getChartAxisEndpointLabels(points, bucketMode),
    [points, bucketMode],
  );
  const unitLabel = getTrendChartUnitLabel(dayCount);

  const { displayHoverIndex, updateHoverAtIndex, clearHover, isHoverSource } = useSyncedChartHover(
    'engagements',
    dayCount,
    bucketMode,
    points,
  );

  const todayX = useMemo(() => {
    const idx = findCurrentPeriodIndex(points, dayCount);
    if (idx == null) return null;
    return chartXAtPeriodIndex(idx, points.length, CHART_WIDTH);
  }, [points, dayCount]);

  const series = useMemo(() => buildSeries(points), [points]);

  const hovered = displayHoverIndex != null ? points[displayHoverIndex] : null;
  const hoverX =
    displayHoverIndex != null && points.length > 0
      ? series.opens[displayHoverIndex]?.x ?? series.clicks[displayHoverIndex]?.x
      : null;
  const hoverXPercent = hoverX != null ? (hoverX / CHART_WIDTH) * 100 : null;

  const pickNearestIndex = (clientX: number, rect: DOMRect) => {
    if (points.length === 0) return null;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const xInViewBox = ratio * CHART_WIDTH;
    let nearest = 0;
    let minDist = Infinity;
    series.opens.forEach((c, i) => {
      const dist = Math.abs(c.x - xInViewBox);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  };

  if (points.length === 0) {
    return (
      <div className={`flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 ${className}`}>
        No engagement data yet
      </div>
    );
  }

  return (
    <div className={`relative w-full min-w-0 min-h-[7.5rem] ${className}`}>
      <div className="absolute inset-0 z-0">
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          preserveAspectRatio="none"
          className="w-full h-full block cursor-crosshair"
          role="img"
          aria-label={
            bucketMode === '5min'
              ? 'Email opens and clicks per 5 minutes over the last hour'
              : bucketMode === 'hourly'
                ? 'Email opens and clicks per hour over the last 6 hours'
                : bucketMode === 'weekly'
                  ? `Email opens and clicks per week over the last ${dayCount} days`
                  : `Email opens and clicks per day over the last ${dayCount} days`
          }
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            updateHoverAtIndex(pickNearestIndex(e.clientX, rect));
          }}
          onMouseLeave={clearHover}
        >
          <defs>
            <linearGradient id="engagementOpensFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(14 165 233)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="rgb(14 165 233)" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {points.map((p, i) =>
            i > 0 ? (
              <line
                key={`eng-day-grid-${p.periodStart}`}
                x1={series.opens[i]?.x ?? 0}
                y1={0}
                x2={series.opens[i]?.x ?? 0}
                y2={CHART_HEIGHT}
                stroke="rgb(203 213 225)"
                strokeWidth="1"
                strokeOpacity={p.isWeekStart ? 0.75 : 0.4}
                vectorEffect="non-scaling-stroke"
              />
            ) : null,
          )}
          <path d={series.opensAreaPath} fill="url(#engagementOpensFill)" />
          <path
            d={series.opensPath}
            fill="none"
            stroke="rgb(14 165 233)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={series.clicksPath}
            fill="none"
            stroke="rgb(16 185 129)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <ChartTodayMarker x={todayX} height={CHART_HEIGHT} />
          {displayHoverIndex != null && hoverX != null ? (
            <ChartHoverHighlight
              x={hoverX}
              height={CHART_HEIGHT}
              dots={[
                {
                  y: series.opens[displayHoverIndex]?.y ?? CHART_HEIGHT,
                  fill: 'rgb(14 165 233)',
                },
                {
                  y: series.clicks[displayHoverIndex]?.y ?? CHART_HEIGHT,
                  fill: 'rgb(16 185 129)',
                },
              ]}
            />
          ) : null}
        </svg>
      </div>

      <div className="absolute top-1.5 left-2 right-2 z-10 pointer-events-none">
        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 drop-shadow-sm">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-0.5 bg-sky-500 rounded" />
            Opens
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-0.5 bg-emerald-500 rounded" />
            Clicks
          </span>
          {unitLabel ? <span>· {unitLabel}</span> : null}
        </div>
      </div>

      {isHoverSource && hovered && hoverXPercent != null ? (
        <div
          className="pointer-events-none absolute top-7 z-20 -translate-x-1/2 rounded-md border border-gray-200 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 px-2 py-1 shadow-sm whitespace-nowrap backdrop-blur-sm"
          style={{ left: `${hoverXPercent}%` }}
        >
          <div className="text-[10px] font-medium text-gray-900 dark:text-gray-100">
            {formatTrendPeriodTooltip(hovered.periodStart, bucketMode)}
          </div>
          <div className="text-[10px] tabular-nums text-gray-600 dark:text-gray-300">
            {hovered.isFuture ? (
              <span className="text-gray-500 dark:text-gray-400">No data yet</span>
            ) : (
              <>
                <span className="text-sky-600 dark:text-sky-400 font-semibold">{hovered.opens}</span> opens
                <span className="mx-1 text-gray-300">·</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{hovered.clicks}</span>{' '}
                clicks
              </>
            )}
          </div>
        </div>
      ) : null}

      <div className="absolute bottom-1 left-2 right-2 z-10 flex justify-between text-[9px] text-gray-400/90 dark:text-gray-500 tabular-nums pointer-events-none">
        <span className="drop-shadow-sm">{axisLabels.start}</span>
        <span className="drop-shadow-sm">{axisLabels.end}</span>
      </div>

      <span className="sr-only">
        Peak daily engagement: {series.maxY.toLocaleString()} events over the chart period.
      </span>
    </div>
  );
};

export default EngagementTrendChart;
